import React from 'react';
import moment from 'moment';

/**
 * Reporte / impresion de un PEDIDO en PDF con react-pdf.
 *
 * Los datos vienen de /api/pedido/notasPicking (fn_pedidos_por_statusdespacho
 * o fn_notasdeentrega_por_statusdespacho):
 *   numpedido/numnotaentrega, totalmonto, codproducto, nomproducto, cantidad,
 *   punitario, ivaproducto, cliente, rif
 *
 * react-pdf se carga en forma diferida desde public/lib/reactpdf.bundle.js
 * (ver tools/reactpdf) porque el webpack 1 de react-scripts 0.9.5 no puede
 * parsear la sintaxis moderna con la que se publica la libreria.
 */

const RUTA_BUNDLE = (process.env.PUBLIC_URL || '') + '/lib/reactpdf.bundle.js';

export const cargarReactPDF = () => {
  if (typeof window === 'undefined') return Promise.reject(new Error('sin navegador'));

  if (window.ReactPDF) return Promise.resolve(window.ReactPDF);

  // se comparte con los demas reportes para no inyectar el bundle dos veces
  if (!window.__reactPdfCarga) {
    window.__reactPdfCarga = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = RUTA_BUNDLE;
      script.async = true;
      script.onload = () => {
        if (window.ReactPDF) {
          resolve(window.ReactPDF);
        } else {
          window.__reactPdfCarga = null;
          reject(new Error('No se pudo inicializar react-pdf'));
        }
      };
      script.onerror = () => {
        window.__reactPdfCarga = null;
        reject(new Error('No se pudo cargar la libreria de PDF (react-pdf)'));
      };
      document.body.appendChild(script);
    });
  }

  return window.__reactPdfCarga;
};

const texto = (valor) => {
  if (valor === undefined || valor === null) return '';
  if (valor instanceof Date) return moment(valor).format('DD-MM-YYYY');
  if (typeof valor === 'object') {
    try {
      return JSON.stringify(valor);
    } catch (e) {
      return '';
    }
  }
  return String(valor);
};

const numero = (valor) => {
  if (valor === undefined || valor === null) return null;
  const n = parseFloat(String(valor).trim());
  return isNaN(n) ? null : n;
};

const conValor = (valor) => valor !== undefined && valor !== null && String(valor).trim() !== '';

// cantidad sin decimales innecesarios (5.00 -> 5)
const formatearCantidad = (valor) => {
  const n = numero(valor);
  if (n === null) return '';
  return n
    .toFixed(2)
    .replace(/(\.\d*?)0+$/, '$1')
    .replace(/\.$/, '');
};

// montos en formato 1.234,56
export const formatearMonto = (valor) => {
  const n = numero(valor);
  if (n === null) return '';
  const partes = Math.abs(n).toFixed(2).split('.');
  const entero = partes[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return (n < 0 ? '-' : '') + entero + ',' + partes[1];
};

export const formatearMontoIva = (valor) => {
  const n = numero(valor);
  if (n === null) return '';
  const partes = Math.abs(n).toFixed(4).split('.');
  const entero = partes[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return (n < 0 ? '-' : '') + entero + ',' + partes[1];
};

// el backend puede devolver los campos con prefijo txt
const sinPrefijo = (clave) => String(clave).toLowerCase().replace(/^txt/, '');

const indexarFila = (fila) => {
  const indice = {};
  Object.keys(fila).forEach((clave) => {
    const normal = sinPrefijo(clave);
    if (indice[normal] === undefined) indice[normal] = clave;
  });
  return indice;
};

const valorCampo = (fila, indice, claves) => {
  for (let i = 0; i < claves.length; i++) {
    const clave = claves[i];

    // permite patrones, para tolerar variantes del nombre del campo
    if (clave instanceof RegExp) {
      const nombres = Object.keys(indice);
      for (let j = 0; j < nombres.length; j++) {
        if (clave.test(nombres[j]) && conValor(fila[indice[nombres[j]]])) {
          return fila[indice[nombres[j]]];
        }
      }
      continue;
    }

    const encontrada = indice[clave];
    if (encontrada !== undefined && conValor(fila[encontrada])) return fila[encontrada];
  }
  return null;
};
// Campos del API usados por el reporte (con alias por si cambian de nombre)
const CAMPOS = {
  numero: ['numpedido', 'numpedidoc', 'numeropedido', 'numnotaentrega', 'numeronotaentrega'],
  cliente: ['cliente', 'nombrecliente', 'nomcliente'],
  rif: ['rif', 'rifcliente', 'rifclientec'],
  codigo: ['codigo', 'codproducto', 'codigoproducto'],
  nombre: ['nombrecorto', 'nomproducto', 'nombreproducto', 'nombre', 'descripcion'],
  cantidad: ['cantidad', 'cantidadpedida'],
  tipoUnidad: ['tipounidad', 'tipo_unidad', /tipo_?unidad/],
  unidadPrincipal: [
    'unidadprincipal',
    'unidaprincipal',
    /unida?d?_?principal/,
    'unidadmedida',
    'descripcionunidad',
    'unidad',
  ],
  unidadAlterna: ['unidadalterna', 'unidad_alterna', /unida?d?_?alterna/],
  punitario: ['punitario', 'precio', 'preciounitario'],
  iva: ['ivaproducto', 'iva', 'montoiva'],
  total: ['totalmonto', 'total', 'montototal'],
};

// si el API manda la letra (B, C, ...) se muestra el nombre de la unidad
const LETRAS_UNIDAD = {
  B: 'BULTO',
  C: 'CAJA',
  P: 'PAQUETE',
  D: 'DOCENA',
  U: 'UNIDAD',
};

// 15 BULTO -> 15 BULTOS ; 15 UNIDAD -> 15 UNIDADES ; 1 CAJAS -> 1 CAJA
const concordarUnidad = (nombre, cantidad) => {
  const palabra = texto(nombre).trim().toUpperCase();
  if (palabra === '') return '';

  const n = numero(cantidad);
  if (n === null) return palabra;

  if (n === 1) {
    if (/ES$/.test(palabra)) return palabra.substring(0, palabra.length - 2);
    if (/S$/.test(palabra)) return palabra.substring(0, palabra.length - 1);
    return palabra;
  }

  if (/S$/.test(palabra)) return palabra;
  if (/[AEIOU]$/.test(palabra)) return palabra + 'S';
  return palabra + 'ES';
};

/**
 * Unidad que se muestra en la cantidad:
 *   tipounidad = 0 -> unidad principal del producto (ej. BULTOS)
 *   tipounidad = 1 -> unidad alterna (ej. UNIDADES)
 */
export const unidadDeFila = (tipounidad, unidadPrincipal, unidadAlterna, cantidad) => {
  let nombre = '';

  if (tipounidad === 1) {
    nombre = texto(unidadAlterna).trim();
    if (nombre === '') nombre = 'UNIDAD';
  } else {
    nombre = texto(unidadPrincipal).trim();
  }

  const letra = nombre.toUpperCase();
  if (nombre.length === 1 && LETRAS_UNIDAD[letra]) nombre = LETRAS_UNIDAD[letra];

  return concordarUnidad(nombre, cantidad);
};

/**
 * Normaliza los datos de selectPedidos
 * (acepta un arreglo de renglones o un solo objeto).
 */
export const normalizarPedido = (datos) => {
  const entrada = Array.isArray(datos) ? datos : datos ? [datos] : [];
  const cabecera = { numero: '', cliente: '', rif: '', total: null };
  const filas = [];

  entrada.forEach((registro) => {
    const fila = registro || {};
    const indice = indexarFila(fila);
    const valorDe = (claves) => valorCampo(fila, indice, claves);

    if (cabecera.numero === '') cabecera.numero = texto(valorDe(CAMPOS.numero));
    if (cabecera.cliente === '') cabecera.cliente = texto(valorDe(CAMPOS.cliente));
    if (cabecera.rif === '') cabecera.rif = texto(valorDe(CAMPOS.rif));

    const total = numero(valorDe(CAMPOS.total));
    if (total !== null) cabecera.total = total;

    const cantidad = valorDe(CAMPOS.cantidad);
    const unidad = unidadDeFila(
      numero(valorDe(CAMPOS.tipoUnidad)),
      valorDe(CAMPOS.unidadPrincipal),
      valorDe(CAMPOS.unidadAlterna),
      cantidad
    );
    const cantidadTexto = formatearCantidad(cantidad);

    filas.push({
      codigo: texto(valorDe(CAMPOS.codigo)),
      nombre: texto(valorDe(CAMPOS.nombre)),
      cantidad:
        cantidadTexto === '' || unidad === '' ? cantidadTexto : cantidadTexto + ' ' + unidad,
      punitario: formatearMonto(valorDe(CAMPOS.punitario)),
      iva: formatearMontoIva(valorDe(CAMPOS.iva)),
    });
  });

  return { filas: filas, cabecera: cabecera };
};

const crearEstilos = (StyleSheet) =>
  StyleSheet.create({
    pagina: {
      paddingTop: 30,
      paddingBottom: 34,
      paddingHorizontal: 24,
      fontSize: 8,
      fontFamily: 'Helvetica',
      color: '#232323',
    },
    encabezado: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-end',
      borderBottomWidth: 1.5,
      borderBottomColor: '#0b3d63',
      paddingBottom: 5,
    },
    titulo: {
      fontSize: 15,
      fontFamily: 'Helvetica-Bold',
      color: '#0b3d63',
    },
    subtitulo: {
      fontSize: 8,
      color: '#4d4d4d',
    },
    datos: {
      borderWidth: 1,
      borderColor: '#d5dee6',
      backgroundColor: '#f2f6fa',
      paddingVertical: 4,
      paddingHorizontal: 6,
      marginBottom: 8,
    },
    datosFila: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    datosTexto: {
      fontSize: 8,
      fontFamily: 'Helvetica-Bold',
      color: '#0b3d63',
    },
    datosPie: {
      fontSize: 7.5,
      color: '#4d4d4d',
      marginTop: 2,
    },
    filaTitulos: {
      flexDirection: 'row',
      backgroundColor: '#0b3d63',
    },
    tituloColumna: {
      fontSize: 7.5,
      fontFamily: 'Helvetica-Bold',
      color: '#ffffff',
      paddingVertical: 4,
      paddingHorizontal: 3,
    },
    fila: {
      flexDirection: 'row',
      borderBottomWidth: 0.5,
      borderBottomColor: '#dfe6ec',
    },
    filaPar: {
      backgroundColor: '#f7f9fb',
    },
    celda: {
      fontSize: 7.5,
      paddingVertical: 3,
      paddingHorizontal: 3,
    },
    derecha: {
      textAlign: 'right',
    },
    totalCaja: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      marginTop: 6,
      paddingRight: 3,
    },
    totalTexto: {
      fontSize: 10,
      fontFamily: 'Helvetica-Bold',
      color: '#0b3d63',
    },
    firmas: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginTop: 28,
    },
    firma: {
      width: '30%',
      borderTopWidth: 0.8,
      borderTopColor: '#8a99a8',
      paddingTop: 3,
    },
    firmaTexto: {
      fontSize: 7,
      color: '#5c6b7a',
      textAlign: 'center',
    },
    pieFijo: {
      position: 'absolute',
      bottom: 14,
      left: 24,
      right: 24,
      flexDirection: 'row',
      justifyContent: 'space-between',
      borderTopWidth: 0.5,
      borderTopColor: '#d5dee6',
      paddingTop: 3,
    },
    pieTexto: {
      fontSize: 7,
      color: '#6c757d',
    },
    sinDatos: {
      marginTop: 40,
      textAlign: 'center',
      fontSize: 10,
      color: '#6c757d',
    },
  });
/**
 * Documento react-pdf de un pedido. R = window.ReactPDF
 *
 * datos     : filas de /api/pedido/notasPicking
 * metadatos : { empresa, usuario, numpedido, tipoDocumento }
 */
export const crearDocumentoPedido = (R, datos, metadatos) => {
  const { Document, Page, View, Text } = R;
  const estilos = crearEstilos(R.StyleSheet);
  const info = metadatos || {};
  const { filas, cabecera } = normalizarPedido(datos);
  const numero = cabecera.numero !== '' ? cabecera.numero : texto(info.numpedido);
  const tipoDocumento = texto(info.tipoDocumento || 'PEDIDO').toUpperCase();

  return (
    <Document
      title={tipoDocumento + ' ' + numero}
      author={texto(info.empresa)}
      subject={tipoDocumento}
      creator="ATHOS ERP"
      producer="ATHOS ERP"
    >
      <Page size="A4" orientation="portrait" style={estilos.pagina}>
        <View style={estilos.encabezado} fixed>
          <View>
            <Text style={estilos.titulo}>{tipoDocumento + ' N° ' + numero}</Text>
            <Text style={estilos.subtitulo}>{texto(info.empresa)}</Text>
          </View>
          <View>
            <Text style={estilos.subtitulo}>
              {'Emitido: ' + moment().format('DD-MM-YYYY hh:mm a')}
            </Text>
            <Text style={estilos.subtitulo}>{'Usuario: ' + texto(info.usuario)}</Text>
          </View>
        </View>

        <View style={estilos.datos}>
          <View style={estilos.datosFila}>
            <Text style={estilos.datosTexto}>{'CLIENTE: ' + cabecera.cliente}</Text>
            <Text style={estilos.datosTexto}>{'RIF: ' + cabecera.rif}</Text>
          </View>
          <Text style={estilos.datosPie}>{'RENGLONES: ' + filas.length}</Text>
        </View>

        <View style={estilos.filaTitulos}>
          <Text style={[estilos.tituloColumna, { flex: 1.2 }]}>CODIGO</Text>
          <Text style={[estilos.tituloColumna, { flex: 4.4 }]}>PRODUCTO</Text>
          <Text style={[estilos.tituloColumna, { flex: 1.1 }, estilos.derecha]}>CANTIDAD</Text>
          <Text style={[estilos.tituloColumna, { flex: 1.4 }, estilos.derecha]}>P. UNITARIO</Text>
          <Text style={[estilos.tituloColumna, { flex: 1.4 }, estilos.derecha]}>IVA</Text>
        </View>

        {filas.length === 0 && (
          <Text style={estilos.sinDatos}>No hay informacion por mostrar</Text>
        )}

        {filas.map((fila, indice) => (
          <View
            key={indice}
            style={[estilos.fila, indice % 2 === 1 ? estilos.filaPar : null]}
            wrap={false}
          >
            <Text style={[estilos.celda, { flex: 1.2 }]}>{fila.codigo}</Text>
            <Text style={[estilos.celda, { flex: 4.4 }]}>{fila.nombre}</Text>
            <Text style={[estilos.celda, { flex: 1.1 }, estilos.derecha]}>{fila.cantidad}</Text>
            <Text style={[estilos.celda, { flex: 1.4 }, estilos.derecha]}>{fila.punitario}</Text>
            <Text style={[estilos.celda, { flex: 1.4 }, estilos.derecha]}>{fila.iva}</Text>
          </View>
        ))}

        {cabecera.total !== null && (
          <View style={estilos.totalCaja}>
            <Text style={estilos.totalTexto}>
              {'TOTAL ' + tipoDocumento + ': ' + formatearMonto(cabecera.total)}
            </Text>
          </View>
        )}

        <View style={estilos.firmas} wrap={false}>
          <View style={estilos.firma}>
            <Text style={estilos.firmaTexto}>PREPARADO POR</Text>
          </View>
          <View style={estilos.firma}>
            <Text style={estilos.firmaTexto}>VERIFICADO POR</Text>
          </View>
          <View style={estilos.firma}>
            <Text style={estilos.firmaTexto}>RECIBIDO POR</Text>
          </View>
        </View>

        <View style={estilos.pieFijo} fixed>
          <Text style={estilos.pieTexto}>{tipoDocumento + ' N° ' + numero}</Text>
          <Text
            style={estilos.pieTexto}
            render={({ pageNumber, totalPages }) =>
              'Pagina ' + pageNumber + ' de ' + totalPages
            }
          />
        </View>
      </Page>
    </Document>
  );
};

/**
 * Genera el PDF del pedido y lo muestra en la ventana indicada
 * (si no se pudo abrir, lo descarga).
 */
export const imprimirPedido = async (datos, metadatos, ventana) => {
  const R = await cargarReactPDF();
  const documento = crearDocumentoPedido(R, datos, metadatos);
  const blob = await R.pdf(documento).toBlob();
  const url = URL.createObjectURL(blob);

  if (ventana && !ventana.closed) {
    ventana.location.href = url;
  } else {
    const enlace = document.createElement('a');
    enlace.href = url;
    const tipoDocumento = texto((metadatos || {}).tipoDocumento || 'Pedido');
    const nombreArchivo = tipoDocumento.replace(/[^a-zA-Z0-9]+/g, '-');
    enlace.download = nombreArchivo + '-' + moment().format('DD-MM-YYYY-hhmm') + '.pdf';
    document.body.appendChild(enlace);
    enlace.click();
    document.body.removeChild(enlace);
  }

  setTimeout(() => URL.revokeObjectURL(url), 120000);
};



// WEBPACK FOOTER //
// ./src/components/ventas/reportePedidoPDF.js